import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Card,
  IconButton,
  InputAdornment,
  Tab,
  TablePagination,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import { Icon } from "@iconify/react";
import roundFilterList from "@iconify/icons-ic/round-filter-list";
import ClientReviewsTable from "src/components/ClientReviews/ClientReviewsTable";
import {
  CountTabLabel,
  ReviewsEmpty,
  ReviewsSkeleton,
} from "src/components/ClientReviews/ReviewParts";
import CustomDrawer from "src/components/DrawerForm/CustomDrawer";
import FilteredChipNew from "src/components/FilteredChipNew";
import { clientReviewsApi } from "src/DAL/member/ClientReviews";
import ClientReviewsFilter from "./ClientReviewsFilter";
import useDueDateFilter, { EMPTY_FILTER } from "./useDueDateFilter";

const TABS = [
  { value: "all", label: "All", empty: { title: "No reviews yet", text: "Set a review date on a member's programme to see it here." } },
  { value: "today", label: "Due today", tone: "warning", empty: { title: "Nothing due today", text: "No reviews are due today." } },
  { value: "overdue", label: "Overdue", tone: "error", empty: { title: "Nothing overdue", text: "Every review is on time." } },
];

const useDebouncedValue = (value, delay) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
};

/** Coach portal: every client programme review date ("View all" on the dashboard card). */
export default function ClientReviews() {
  const navigate = useNavigate();
  const location = useLocation();
  // Opens on the dashboard card's tab ("all" or "overdue").
  const [scope, setScope] = useState(
    TABS.some((t) => t.value === location.state?.tab) ? location.state.tab : "all"
  );
  const [searchText, setSearchText] = useState("");
  const search = useDebouncedValue(searchText.trim(), 350);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const filter = useDueDateFilter(() => setPage(0));

  useEffect(() => setPage(0), [search]);

  // The due date filter only applies to the All tab.
  const range = scope === "all" ? filter.range : { from: "", to: "" };
  const hasRange = Boolean(range.from || range.to);

  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey: ["clientReviews", scope, range.from, range.to, search, page, rowsPerPage],
    queryFn: async () => {
      const result = await clientReviewsApi({
        scope,
        search,
        page,
        limit: rowsPerPage,
        from: range.from,
        to: range.to,
      });
      if (result?.code !== 200) throw new Error(result?.message);
      return result;
    },
    keepPreviousData: true,
  });

  const counts = data?.counts || {};
  const rows = data?.rows || [];
  const tab = TABS.find((t) => t.value === scope);

  let empty = tab.empty;
  if (search) empty = { title: "No matches", text: `No members match "${search}".` };
  else if (hasRange) empty = { title: "Nothing in this period", text: "No reviews are due in the selected dates." };

  let body;
  if (isLoading) body = <ReviewsSkeleton rows={6} />;
  else if (isError) body = <Alert severity="error" sx={{ m: 2 }}>Could not load client reviews.</Alert>;
  else if (!rows.length) body = <ReviewsEmpty {...empty} />;
  else {
    body = (
      <Box sx={{ opacity: isFetching ? 0.6 : 1, transition: "opacity .2s" }}>
        <ClientReviewsTable rows={rows} />
      </Box>
    );
  }

  return (
    <>
      <div className="container">
        <div className="row mb-3 mt-2">
          <div className="col-lg-8 col-sm-12">
            <h2>
              <IconButton className="back-screen-button m-0" onClick={() => navigate(-1)}>
                <ArrowBackIcon className="me-2" />
              </IconButton>
              Client reviews
            </h2>
            <Typography variant="body2" color="text.secondary">
              Programme review dates of your members, closest to today first. Open a member to
              review them.
            </Typography>
          </div>
          {scope === "all" && (
            <div className="col-lg-4 col-sm-12 text-end">
              <button className="small-contained-button mt-1 mb-3" onClick={filter.open}>
                Filters &nbsp;&nbsp; <Icon icon={roundFilterList} />
              </button>
            </div>
          )}
        </div>

        {scope === "all" && (
          <FilteredChipNew
            data={filter.applied}
            tempState={filter.applied}
            EMPTY_FILTER={EMPTY_FILTER}
            onDeleteChip={filter.removeChip}
            onClear={filter.clear}
          />
        )}

        <Card sx={{ borderRadius: 2, boxShadow: 2, mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1,
              px: 2,
              pt: 1,
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Tabs
              value={scope}
              onChange={(e, value) => {
                setScope(value);
                setPage(0);
              }}
              variant="scrollable"
              allowScrollButtonsMobile
            >
              {TABS.map((t) => (
                <Tab
                  key={t.value}
                  value={t.value}
                  label={
                    <CountTabLabel label={t.label} count={data ? counts[t.value] || 0 : undefined} tone={t.tone} />
                  }
                  sx={{ textTransform: "none", minHeight: 44, px: 1, mr: 1 }}
                />
              ))}
            </Tabs>
            <TextField
              size="small"
              placeholder="Search member"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "text.disabled" }} />
                  </InputAdornment>
                ),
              }}
              sx={{ width: { xs: "100%", sm: 260 }, mb: { xs: 1, sm: 0 } }}
            />
          </Box>

          {body}

          {rows.length > 0 && (
            <TablePagination
              component="div"
              rowsPerPageOptions={[10, 25, 50, 100]}
              count={data?.total || 0}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, value) => setPage(value)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              sx={{ borderTop: 1, borderColor: "divider" }}
            />
          )}
        </Card>
      </div>

      <CustomDrawer
        isOpenDrawer={filter.isOpen}
        onOpenDrawer={filter.open}
        onCloseDrawer={filter.close}
        pageTitle="Filters"
        componentToPassDown={
          <ClientReviewsFilter
            filterData={filter.form}
            handleChangeOthers={filter.change}
            searchSubmitFilter={filter.submit}
            handleClearFilter={filter.clear}
          />
        }
      />
    </>
  );
}
